import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, LineChart, Loader2, RefreshCw, FileBarChart } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { SubcategoryInsightsModal } from "./SubcategoryInsightsModal";
import { SubcategoryChartModal } from "./SubcategoryChartModal";
import { useAnalyzeResults } from "@/hooks/useAnalyzeResults";

type ReportPeriod = "current_month" | "current_year" | "last_year" | "last_3_years" | "all";

interface LabResult {
  id: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string;
  source_document_id: string;
  source_session_id?: string;
  subcategory_id: string;
}

interface ResultCell {
  value: number | null;
  unit: string | null;
  testName: string;
  documentId: string;
  sessionId?: string;
  resultId: string;
  collectedAt: string;
  refLow: number | null;
  refHigh: number | null;
}

interface SubcategoryData {
  id: string;
  name: string;
  resultsByMonth: {
    [monthYear: string]: ResultCell[];
  };
  allResults: LabResult[];
}

interface ReportData {
  categoryId: string;
  categoryName: string;
  subcategories: SubcategoryData[];
}

interface LabResultsReportProps {
  patientId: string;
  onBack: () => void;
}

const getDateRangeForPeriod = (period: ReportPeriod): { start: Date | null; end: Date } => {
  const now = new Date();
  const end = now;

  switch (period) {
    case "current_month": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start, end };
    }
    case "current_year": {
      const start = new Date(now.getFullYear(), 0, 1);
      return { start, end };
    }
    case "last_year": {
      const start = new Date(now.getFullYear() - 1, 0, 1);
      const end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
      return { start, end };
    }
    case "last_3_years": {
      const start = new Date(now.getFullYear() - 3, 0, 1);
      return { start, end };
    }
    case "all":
    default:
      return { start: null, end };
  }
};

const filterResultsByPeriod = (results: LabResult[], period: ReportPeriod): LabResult[] => {
  if (period === "all") return results;

  const { start, end } = getDateRangeForPeriod(period);

  return results.filter((result) => {
    if (!result.collected_at) return false;
    const resultDate = new Date(result.collected_at);
    return (!start || resultDate >= start) && resultDate <= end;
  });
};

export const LabResultsReport = ({ patientId, onBack }: LabResultsReportProps) => {
  const [reportPeriod, setReportPeriod] = useState<ReportPeriod>("all");
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState<SubcategoryData | null>(null);
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [showChartModal, setShowChartModal] = useState(false);

  const navigate = useNavigate();
  const analyzeMutation = useAnalyzeResults();

  useEffect(() => {
    loadReportData();
  }, [patientId, reportPeriod]);


  const loadReportData = async () => {
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // 1. Buscar todas as subcategorias do usuário
      const { data: subcategoriesData, error: subError } = await supabase
        .from("lab_subcategories")
        .select(`
          id,
          name,
          category_id,
          lab_categories(id, name, display_order)
        `)
        .eq("user_id", user.id);

      if (subError) throw subError;

      // 2. Buscar todos os resultados do paciente
      const { data: allResults, error: resultsError } = await supabase
        .from("lab_results_onboarding")
        .select(`
          id,
          test_name,
          value,
          unit,
          ref_low,
          ref_high,
          collected_at,
          source_document_id,
          source_session_id,
          subcategory_id
        `)
        .eq("patient_id", patientId)
        .not("subcategory_id", "is", null)
        .order("collected_at", { ascending: true });

      if (resultsError) throw resultsError;

      // 3. Aplicar filtro de período
      const filteredResults = filterResultsByPeriod(allResults || [], reportPeriod);

      // 4. Agrupar por subcategoria
      const resultsBySubcategory: Record<string, LabResult[]> = {};
      filteredResults.forEach((result) => {
        if (!resultsBySubcategory[result.subcategory_id]) {
          resultsBySubcategory[result.subcategory_id] = [];
        }
        resultsBySubcategory[result.subcategory_id].push(result);
      });

      // 5. Construir estrutura de dados para a tabela
      const categoriesMap = new Map<string, ReportData>();

      subcategoriesData?.forEach((sub: any) => {
        const results = resultsBySubcategory[sub.id] || [];

        // Filtrar subcategorias sem resultados no período
        if (results.length === 0) return;

        const category = sub.lab_categories;

        if (!categoriesMap.has(category.id)) {
          categoriesMap.set(category.id, {
            categoryId: category.id,
            categoryName: category.name,
            subcategories: [],
          });
        }

        // Agrupar resultados por mês/ano
        const resultsByMonth: Record<string, ResultCell[]> = {};

        results.forEach((result) => {
          const monthYear = format(new Date(result.collected_at), "MM/yyyy");

          if (!resultsByMonth[monthYear]) {
            resultsByMonth[monthYear] = [];
          }

          resultsByMonth[monthYear].push({
            value: result.value,
            unit: result.unit,
            testName: result.test_name,
            documentId: result.source_document_id,
            sessionId: result.source_session_id,
            resultId: result.id,
            collectedAt: result.collected_at,
            refLow: result.ref_low,
            refHigh: result.ref_high,
          });
        });

        categoriesMap.get(category.id)!.subcategories.push({
          id: sub.id,
          name: sub.name,
          resultsByMonth,
          allResults: results,
        });
      });

      // Ordenar categorias e subcategorias
      const report = Array.from(categoriesMap.values()).sort((a, b) =>
        a.categoryName.localeCompare(b.categoryName)
      );

      setReportData(report);
    } catch (error) {
      console.error("Error loading report data:", error);
      toast.error("Erro ao carregar relatório");
    } finally {
      setLoading(false);
    }
  };

  const getAllMonths = (): string[] => {
    const monthsSet = new Set<string>();

    reportData.forEach((category) => {
      category.subcategories.forEach((sub) => {
        Object.keys(sub.resultsByMonth).forEach((month) => monthsSet.add(month));
      });
    });

    // Ordenar cronologicamente
    return Array.from(monthsSet).sort((a, b) => {
      const [monthA, yearA] = a.split("/").map(Number);
      const [monthB, yearB] = b.split("/").map(Number);
      return yearA !== yearB ? yearA - yearB : monthA - monthB;
    });
  };

  const handleOpenDocument = (documentId: string, sessionId?: string) => {
    try {
      if (sessionId) {
        navigate(`/app/smartfile/${sessionId}`);
      } else if (documentId) {
        navigate(`/app/pacientes/${patientId}?document=${documentId}`);
      }
    } catch (error) {
      console.error("Error navigating to document:", error);
      toast.error("Erro ao abrir documento");
    }
  };

  const handleOpenInsights = (sub: SubcategoryData) => {
    setSelectedSubcategory(sub);
    setShowInsightsModal(true);
  };

  const handleOpenChart = (sub: SubcategoryData) => {
    setSelectedSubcategory(sub);
    setShowChartModal(true);
  };

  const isOutOfRange = (cell: ResultCell): boolean => {
    if (cell.value === null) return false;
    if (cell.refLow !== null && cell.value < cell.refLow) return true;
    if (cell.refHigh !== null && cell.value > cell.refHigh) return true;
    return false;
  };

  const handleUpdateResults = async () => {
    try {
      await analyzeMutation.mutateAsync({ patientId });
      await loadReportData();
    } catch (error) {
      console.error("Error analyzing results:", error);
    }
  };

  const months = getAllMonths();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full max-w-full overflow-x-hidden">
      {/* Header com filtro */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 w-full">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <FileBarChart className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
          <h2 className="text-base sm:text-lg font-semibold truncate">Análise Temporal de Resultados</h2>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center w-full sm:w-auto">
          <Select value={reportPeriod} onValueChange={(value) => setReportPeriod(value as ReportPeriod)}>
            <SelectTrigger className="w-full sm:w-48 text-xs sm:text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current_month">Mês atual</SelectItem>
              <SelectItem value="current_year">Este ano</SelectItem>
              <SelectItem value="last_year">Último ano</SelectItem>
              <SelectItem value="last_3_years">Últimos 3 anos</SelectItem>
              <SelectItem value="all">Todos os períodos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>


      {/* Cards */}
      {reportData.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground text-sm">
              Nenhum resultado encontrado para o período selecionado.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4 sm:space-y-6 w-full max-w-full">
          {reportData.map((category) => (
            <Card key={category.categoryId} className="overflow-hidden w-full">
              <CardHeader className="bg-muted/30 pb-3 px-4 sm:px-6">
                <CardTitle className="text-base sm:text-lg font-semibold break-words">{category.categoryName}</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6 pb-4 sm:pb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 w-full">
                  {category.subcategories.map((sub) => {
                    // Coletar todos os resultados de todos os meses
                    const allResults: Array<{ cell: ResultCell; month: string }> = [];
                    months.forEach((month) => {
                      const cells = sub.resultsByMonth[month] || [];
                      cells.forEach((cell) => {
                        allResults.push({ cell, month });
                      });
                    });

                    return (
                      <Card key={sub.id} className="border-2 hover:border-primary/30 transition-colors w-full min-w-0 h-[200px] flex flex-col">
                        <CardHeader className="pb-2 px-3 sm:px-4 pt-3 flex-shrink-0">
                          <CardTitle className="text-xs sm:text-sm font-medium break-words line-clamp-1">{sub.name}</CardTitle>
                        </CardHeader>
                        <CardContent className="flex-1 flex flex-col px-3 sm:px-4 pb-3 min-h-0">
                          {/* Todos os resultados horizontalmente com scroll */}
                          <div className="flex-1 overflow-x-auto overflow-y-hidden min-h-0">
                            <div className="flex flex-nowrap gap-1.5 sm:gap-2 items-start h-full pb-2">
                              {allResults.map(({ cell, month }, idx) => (
                                <div key={idx} className="flex flex-col items-start flex-shrink-0">
                                  <div className="text-[9px] sm:text-[10px] text-muted-foreground mb-0.5 whitespace-nowrap">
                                    {month}
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className={cn(
                                      "h-auto py-1 px-2 sm:py-1.5 sm:px-3 text-[10px] sm:text-xs hover:bg-muted transition-colors flex-shrink-0 whitespace-nowrap",
                                      isOutOfRange(cell) && "border-destructive/50 bg-destructive/5 text-destructive font-semibold"
                                    )}
                                    onClick={() => handleOpenDocument(cell.documentId, cell.sessionId)}
                                  >
                                    <span className="font-medium">
                                      {cell.value !== null ? cell.value.toFixed(1) : "—"}
                                    </span>
                                    {cell.unit && (
                                      <span className="text-muted-foreground ml-0.5 sm:ml-1 text-[9px] sm:text-[10px]">
                                        {cell.unit}
                                      </span>
                                    )}
                                  </Button>
                                </div>
                              ))}
                              {allResults.length === 0 && (
                                <p className="text-xs text-muted-foreground py-4 whitespace-nowrap">Nenhum resultado disponível</p>
                              )}
                            </div>
                          </div>

                          {/* Botões de ação fixos no rodapé */}
                          <div className="flex gap-1.5 sm:gap-2 pt-2 border-t mt-2 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenInsights(sub)}
                              className="flex-1 text-[10px] sm:text-xs h-7 sm:h-8 px-2"
                            >
                              <Sparkles className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1 sm:mr-1.5" />
                              <span className="hidden sm:inline">Insights</span>
                              <span className="sm:hidden">IA</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenChart(sub)}
                              className="flex-1 text-[10px] sm:text-xs h-7 sm:h-8 px-2"
                            >
                              <LineChart className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1 sm:mr-1.5" />
                              Gráfico
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modais */}
      {showInsightsModal && selectedSubcategory && (
        <SubcategoryInsightsModal
          subcategory={selectedSubcategory}
          onClose={() => {
            setShowInsightsModal(false);
            setSelectedSubcategory(null);
          }}
        />
      )}

      {showChartModal && selectedSubcategory && (
        <SubcategoryChartModal
          subcategory={selectedSubcategory}
          period={reportPeriod}
          onClose={() => {
            setShowChartModal(false);
            setSelectedSubcategory(null);
          }}
        />
      )}
    </div>
  );
};
